'use client';

import { useState, useEffect } from 'react';
import { requestPasswordReset, verifyResetCode, resetPassword } from '@/lib/api-client';
import Spinner from './Spinner';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback to open login modal after success
}

export default function ForgotPasswordModal({ isOpen, onClose, onSuccess }: ForgotPasswordModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setTimeout(() => setMounted(false), 300);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setEmail('');
        setCode('');
        setResetToken('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
      }, 300);
    }
  }, [isOpen]);

  // Step 1: Request reset code
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await requestPasswordReset(email);
      
      if (response.success) {
        setStep(2);
      } else {
        setError(response.error || 'Failed to send reset code');
      }
    } catch (err: any) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await verifyResetCode(email, code);
      
      if (response.success && response.resetToken) {
        setResetToken(response.resetToken);
        setStep(3);
      } else {
        setError(response.error || 'Invalid or expired code');
      }
    } catch (err: any) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await resetPassword(resetToken, newPassword);
      
      if (response.success) {
        // Success! Show success message and close modal
        setError('');
        // Wait a moment to show success, then close and trigger login
        setTimeout(() => {
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        }, 1500);
      } else {
        setError(response.error || 'Failed to reset password');
      }
    } catch (err: any) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!mounted && !isOpen) return null;

  const getStepTitle = () => {
    switch (step) {
      case 1: return 'reset password';
      case 2: return 'verify code';
      case 3: return 'new password';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1: return 'Enter your email to receive a verification code';
      case 2: return `Enter the 6-digit code sent to ${email}`;
      case 3: return 'Choose a new password for your account';
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 transition-all duration-300 ease-out ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/80 backdrop-blur-sm transition-all duration-300 ease-out ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className={`relative bg-[#0f0f0f] rounded-lg p-4 sm:p-5 w-full max-w-[340px] shadow-2xl transition-all duration-300 ease-out max-h-[90vh] overflow-y-auto ${
          isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-90 opacity-0 translate-y-8'
        }`}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-white/50 hover:text-white/90 transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-3 sm:mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-white">
            {getStepTitle()}
          </h2>
          <p className="text-[10px] sm:text-xs text-white/50 mt-1">
            {getStepDescription()}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className={`w-2 h-2 rounded-full transition-colors ${step >= 1 ? 'bg-purple-500' : 'bg-white/20'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${step >= 2 ? 'bg-purple-500' : 'bg-white/20'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${step >= 3 ? 'bg-purple-500' : 'bg-white/20'}`} />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-3 p-2 bg-red-500/20 rounded-lg text-red-400 text-[11px] sm:text-xs animate-fadeIn">
            {error}
          </div>
        )}

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={handleRequestReset} className="space-y-2 sm:space-y-2.5">
            <div>
              <label className="block text-xs text-white/70 mb-1">email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 bg-white/5 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 transition-all duration-200"
                placeholder="your@email.com"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-white text-black font-medium rounded-lg hover:bg-white/90 active:scale-95 sm:hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 cursor-pointer flex items-center justify-center gap-2 mt-2 sm:mt-3 text-sm"
            >
              {loading ? (
                <>
                  <Spinner size={16} />
                  <span>sending...</span>
                </>
              ) : (
                'send reset code'
              )}
            </button>
          </form>
        )}

        {/* Step 2: Verification Code */}
        {step === 2 && (
          <form onSubmit={handleVerifyCode} className="space-y-2 sm:space-y-2.5">
            <div>
              <label className="block text-xs text-white/70 mb-1">verification code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                className="w-full px-3 py-2 bg-white/5 rounded-lg text-white text-center text-2xl tracking-widest placeholder:text-white/30 focus:outline-none focus:bg-white/10 transition-all duration-200"
              />
              <p className="text-[10px] sm:text-xs text-white/30 mt-1">
                Check your email for the 6-digit code
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full py-2 bg-white text-black font-medium rounded-lg hover:bg-white/90 active:scale-95 sm:hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 cursor-pointer flex items-center justify-center gap-2 mt-2 sm:mt-3 text-sm"
            >
              {loading ? (
                <>
                  <Spinner size={16} />
                  <span>verifying...</span>
                </>
              ) : (
                'verify code'
              )}
            </button>

            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setError('');
                }}
                className="text-[11px] sm:text-xs text-white/50 hover:text-white/70 transition-colors"
              >
                back
              </button>
            </div>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-2 sm:space-y-2.5">
            <div>
              <label className="block text-xs text-white/70 mb-1">new password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 bg-white/5 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 transition-all duration-200"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label className="block text-xs text-white/70 mb-1">confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 bg-white/5 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 transition-all duration-200"
                placeholder="••••••••"
              />
              <p className="text-[10px] sm:text-xs text-white/30 mt-1">
                Min 8 chars, must include: uppercase, lowercase, number & special char
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-white text-black font-medium rounded-lg hover:bg-white/90 active:scale-95 sm:hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 cursor-pointer flex items-center justify-center gap-2 mt-2 sm:mt-3 text-sm"
            >
              {loading ? (
                <>
                  <Spinner size={16} />
                  <span>resetting...</span>
                </>
              ) : (
                'reset password'
              )}
            </button>
          </form>
        )}

        {/* Success Message (shown briefly before closing) */}
        {step === 3 && !error && !loading && newPassword && confirmPassword && newPassword === confirmPassword && (
          <div className="mt-3 p-2 bg-green-500/20 rounded-lg text-green-400 text-[11px] sm:text-xs text-center animate-fadeIn">
            ✓ Password reset successfully!
          </div>
        )}
      </div>
    </div>
  );
}

